/**
 * App Component - Root application component
 * 
 * This component serves as the main entry point for the application. It sets up:
 * - React Router with routes for Login, Register, Dashboard, and Editor
 * - Protected routes that require authentication
 * - Firebase configuration and initialization
 * 
 * Routes:
 * - /login - Login page
 * - /register - Registration page
 * - /dashboard - User dashboard (protected)
 * - /editor/:portfolioTitle - Blog editor (protected)
 */

import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import TitleUpdater from './components/Common/PageTitleUtil/TitleUpdater';
import Login from './components/Auth/pages/Login';
import Register from './components/Auth/pages/Register';
import Dashboard from './pages/Dashboard';
import ProjectsPage from './components/ProjectsAndSkills/Pages/ProjectsPage';
import DraftListPage from './components/NewBlogSystem/pages/Portfolios/DraftListPage';
import PublishedListPage from './components/NewBlogSystem/pages/Portfolios/PublishedListPage';
import DraftPortfolioRenderer from './components/NewBlogSystem/Renderer/RendererContainers/DraftPortfolioRenderer';
import PublishedPortfolioRenderer from './components/NewBlogSystem/Renderer/RendererContainers/PublishedPortfolioRenderer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DebugImageVisibility from './components/Debug/DebugImageVisibility';
import DebugProjectVisibility from './components/Debug/DebugProjectVisibility';
import DebugSkillVisibility from './components/Debug/DebugSkillVisibility';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminDashboard from './components/Auth/Admin/pages/AdminDashboard';
import DeleteAccount from './components/Auth/pages/DeleteAccount';
import UserManagement from './components/Auth/Admin/pages/UserManagement';
import UserPortfolios from './components/NewBlogSystem/pages/Admin/UserPortfolios';
import UserProjects from './components/ProjectsAndSkills/Pages/Admin/UserProjects';
import UserSkills from './components/ProjectsAndSkills/Pages/Admin/UserSkills';
import { DESIRED_JOBS_CONTEXT_ROUTE } from './navigationRoutes';

function App() {

  return (
    <Router>
      <TitleUpdater />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/debug/images/:userId?" element={<DebugImageVisibility />} />
        <Route path="/debug/images" element={<DebugImageVisibility />} />
        <Route path="/debug/projects/:userId?" element={<DebugProjectVisibility />} />
        <Route path="/debug/projects" element={<DebugProjectVisibility />} />
        <Route path="/debug/skills/:userId?" element={<DebugSkillVisibility />} />
        <Route path="/debug/skills" element={<DebugSkillVisibility />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/portfolios/drafts"
          element={
            <ProtectedRoute>
              <DraftListPage />
            </ProtectedRoute>
          }
        />

          {/* Protected routes */}
        <Route
          path="/portfolios/published"
          element={
            <ProtectedRoute>
              <PublishedListPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobs"
          element={<Navigate to={DESIRED_JOBS_CONTEXT_ROUTE} replace />}
        />

        <Route
          path="/:userId/drafts/editor/:portfolioTitle"
          element={
            <ProtectedRoute>
              <DraftPortfolioRenderer edit={true} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/:userId/drafts/view/:portfolioTitle"
          element={<DraftPortfolioRenderer />}
        />

        <Route
          path="/:userId/published/editor/:portfolioTitle"
          element={
            <ProtectedRoute>
              <PublishedPortfolioRenderer edit={true} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/:userId/published/view/:portfolioTitle"
          element={<PublishedPortfolioRenderer />}
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly={true}>
              <UserManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/:userId/portfolios"
          element={
            <ProtectedRoute adminOnly={true}>
              <UserPortfolios />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/:userId/projects"
          element={
            <ProtectedRoute adminOnly={true}>
              <UserProjects />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/:userId/skills"
          element={
            <ProtectedRoute adminOnly={true}>
              <UserSkills />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/delete-account"
          element={
            <ProtectedRoute>
              <DeleteAccount />
            </ProtectedRoute>
          }
        />

        {/* Default route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
